import _ from 'underscore';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FileItem, FileLikeObject, FileUploader } from 'ng2-file-upload';
import { util } from '../util';
import { AuthService } from '../services/auth/auth.service';
import { DocumentTypeCode, DocumentUploadSource } from '@app/shared/document/document-data.service';

export interface IUploadFormData {
  documentTypeCode?:DocumentTypeCode;
  documentUploadSource?:DocumentUploadSource
}

export interface IUploadFile extends File {
  name:string;
  size:number;
  progress?:number;
  uploaded?:boolean;
  busy?:boolean;
  lastModified:number;
  addedTime:number;
  response:IUploadFileResponse;
  id:number;
  guid?:string;
}

interface IUploadFileResponse {
  status:string;
  message?:string;
  data?:{
    id:number;
    guid:string;
  };
}

export interface IUploadFileCompleteEvent {
  success:boolean;
  files:IUploadFile[];
}

export enum MultiFileUploadStatus {
  BUSY = '',
  OK = 'progress-bar-success',
  ERROR = 'progress-bar-danger'
}

@Component({
  selector: 'multi-file-upload',
  template: `
    <div class="multi-file-upload">
      <div class="drop-zone" ng2FileDrop [uploader]="uploader" (onFileDrop)="addToQueue($event)">
        <div class="drop-label">
          <div *ngIf="!busy">
            <span class="text-muted">Drag and drop your files here or</span>
            <label for="target-select-file" class="btn btn-primary fileinput-button">
              Select files
              <input id="target-select-file" type="file" style="display: none;" ng2FileSelect [uploader]="uploader" (onFileSelected)="addToQueue($event)" multiple />
            </label>
          </div>
          <div *ngIf="busy">
            <h3>
              <activity-indicator></activity-indicator>
              <span class="text-muted">
                Please wait, your files are uploading…
              </span>
            </h3>
          </div>
        </div>
      </div>

      <div *ngIf="files.length">
        <table class="table table-files">
          <tbody *ngFor="let file of files">
            <tr>
              <td class="preview" style="width: 100px;" rowspan="2">
                <img fileUploadPreview [media]="file" />
              </td>
              <td>
                <strong>{{file.name}}</strong>
              </td>
              <td style="width: 10%" nowrap>{{file.size/1024/1024 | number: '1.0-2'}} MB</td>
              <td style="width: 10px" nowrap>
                <a href="" (click)="removeFromQueue(file, $event)" *ngIf="!file.busy">
                  <i class="fa fa-trash"></i>
                </a>
              </td>
            </tr>
            <tr>
              <td colspan="3">
                <div class="progress">
                  <div class="progress-bar" [ngClass]="getFileStatus(file)" role="progressbar" aria-valuenow="40" aria-valuemin="0" aria-valuemax="100" [ngStyle]="{width: file.progress + '%'}">
                    <span class="progress-label" *ngIf="file.progress">
                      <span *ngIf="file.progress !== 100 && file.busy">Uploading {{file.progress | number}}%…</span>
                      <span *ngIf="file.progress === 100 && file.busy">Processing…</span>
                      <span *ngIf="file.progress === 100 && !file.busy && file.uploaded">File upload completed</span>
                      <span *ngIf="file.progress === 100 && !file.busy && !file.uploaded">File upload failed</span>
                    </span>
                  </div>
                </div>
                <span class="text-danger" *ngIf="file.progress === 100 && !file.busy && !file.uploaded && file.response?.message">
                  Error: {{file.response?.message}}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <p *ngIf="!busy">
          <button type="button" class="btn btn-danger btn-xs" (click)="clearQueue()" [disabled]="!files.length">
            <span class="fa fa-trash"></span> Remove all
          </button>
        </p>
        <p *ngIf="!busy && failedFiles.length" class="text-danger">
          The upload has failed. To continue you must remove the files which failed or try again.
        </p>

        <div *ngIf="showButtons">
          <button type="button" class="btn btn-success btn-s margin-right-xs" (click)="uploadFiles()" [disabled]="busy">
            <span class="fa fa-upload"></span> Upload all
          </button>
          <button type="button" class="btn btn-warning btn-s" (click)="abortUploads()" [disabled]="!busy">
            <span class="fa fa-ban"></span> Cancel all
          </button>
        </div>
      </div>

    </div>
  `,
  styleUrls: ['./multi-file-upload.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MultiFileUploadComponent implements OnInit, OnChanges {
  @Input() public path:string;
  @Input() public uploading:boolean;
  @Input() public showButtons:boolean;
  @Input() public filesAccepted:string[];
  @Input() public uploadFormData?:IUploadFormData;

  @Output() public onQueueChange:EventEmitter<IUploadFile[]> = new EventEmitter<IUploadFile[]>();
  @Output() public onUploadCompleted:EventEmitter<IUploadFileCompleteEvent> = new EventEmitter<IUploadFileCompleteEvent>();
  @Output() public onUploadCancelled:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onUploadError:EventEmitter<void> = new EventEmitter<void>();

  public uploader:FileUploader;
  public progress:number;
  public files:IUploadFile[] = [];

  constructor(
    private authService:AuthService,
    private cdr:ChangeDetectorRef
  ) {
    /**/
  }

  public ngOnInit():void {
    // Initialize a new file uploader and configure its options
    this.uploader = new FileUploader({
      url: this.authService.authenticatedUrl(this.path),
      allowedMimeType: this.filesAccepted,
      method: 'POST',
      additionalParameter: util.deepClone(this.uploadFormData) // remove undefined uploadFormData params
    });

    // register uploader handler
    this.uploader.onSuccessItem = this.onFileUploadSuccess;
    this.uploader.onErrorItem = this.onFileUploadError;
    this.uploader.onProgressItem = this.onFileUploadProgress;
    this.uploader.onCompleteAll = this.onFileUploadComplete;
    this.uploader.onCancelItem = this.onFileUploadCancelled;
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.uploading?.currentValue) {
      // start to upload the files (triggered when uploading is true)
      this.uploadFiles();
    } else if (changes.uploading?.currentValue === false) {
      // abort/cancel the upload the files (triggered when uploading is false)
      this.abortUploads();
    }
  }

  public get busy():boolean {
    return _.filter(this.files, (file:IUploadFile) => file.busy).length > 0;
  }

  public get failedFiles():IUploadFile[] {
    return _.filter(this.files, (file:IUploadFile):boolean => file.uploaded === false);
  }

  private get succeedFiles():IUploadFile[] {
    return _.filter(this.files, (file:IUploadFile) => file.uploaded);
  }

  public addToQueue(files?:IUploadFile[]):void {
    _.each(files, (file:IUploadFile) => {
      // add unique identifier
      file.id = this.files.length + 1;

      // prevent adding file duplicates
      const fileInQueue:boolean = !!_.find(this.files, (queued:IUploadFile) => {
        return file.name === queued.name && file.size === queued.size;
      });

      if (!fileInQueue) {
        file.addedTime = util.currentTimeMillis();
        this.files.push(file);
      }
    });

    this.onQueueChange.emit(this.files);
    this.cdr.detectChanges();
  }

  public removeFromQueue(fileToRemove:IUploadFile, event:Event):void {
    event.preventDefault();

    this.files = _.reject(this.files, (file:IUploadFile) => {
      return file.name === fileToRemove.name && file.addedTime === fileToRemove.addedTime;
    });

    this.onQueueChange.emit(this.files);
  }

  public getFileStatus(file:IUploadFile):string {
    if (file.busy) {
      if (file.uploaded) {
        return MultiFileUploadStatus.OK;
      } else {
        return MultiFileUploadStatus.BUSY;
      }
    } else {
      if (!file.uploaded) {
        return MultiFileUploadStatus.ERROR;
      }
    }
    return MultiFileUploadStatus.OK;
  }

  public clearQueue():void {
    this.files = [];
    this.onQueueChange.emit([]);
  }

  public uploadFiles():void {
    this.uploader.queue = [];
    if (this.files.length !== this.succeedFiles.length) {
      // upload all files or only what has been failed
      const filesToUpload:IUploadFile[] = this.failedFiles.length? this.failedFiles : this.files;
      _.forEach(filesToUpload, (file:IUploadFile) => {
        file.busy = true;
        const fileItem:FileItem = new FileItem(this.uploader, file, this.uploader.options);
        // need to add to uploader queue to make it work
        this.uploader.queue.push(fileItem);
      });
      // trigger upload all files
      this.uploader.uploadAll();
    } else {
      this.onFileUploadComplete();
    }
  }

  public abortUploads():void {
    if (this.busy) {
      this.uploader.cancelAll();
      this.uploader.clearQueue();
      this.onUploadCancelled.emit();
    }
  }

  private calculateProgress():void {
    if (this.files && this.files.length) {
      const sum:number = _.reduce(this.files, (m:number, file:IUploadFile) => m + this.getProgress(file), 0);
      this.progress = sum / this.files.length;
    }
  }

  private getProgress(file:IUploadFile):number {
    return file.progress? file.progress : 0;
  }

  private getFile(file:FileLikeObject):IUploadFile {
    return _.findWhere(this.files, { name: file.name });
  }

  private onFileUploadSuccess = (fileItem:FileItem, response:string):void => {
    try {
      this.getFile(fileItem.file).response = JSON.parse(response);
    } catch(e) { /**/ }
    this.getFile(fileItem.file).uploaded = this.getFile(fileItem.file).response?.status === 'OK';
    this.getFile(fileItem.file).busy = false;
  };

  private onFileUploadError = (fileItem:FileItem, response:string):void => {
    try {
      this.getFile(fileItem.file).response = JSON.parse(response);
    } catch(e) { /**/ }
    this.getFile(fileItem.file).uploaded = false;
    this.getFile(fileItem.file).busy = false;
  };

  private onFileUploadProgress = (fileItem:FileItem, progress:number):void => {
    this.getFile(fileItem.file).progress = progress;
    this.calculateProgress();
  };

  private onFileUploadComplete = ():void => {
    // do not emit the completion event for the upload if any file fail to upload, instead emit an upload error signal
    if(!this.failedFiles?.length){
      this.onUploadCompleted.emit({
        success: !this.failedFiles.length,
        files: this.files
      });
    } else {
      this.onUploadError.emit();
    }
  };

  private onFileUploadCancelled = (fileItem:FileItem):void => {
    this.getFile(fileItem.file).busy = false;
    this.getFile(fileItem.file).uploaded = false;
  };
}